import {
  createSlice,
  createAsyncThunk,
  isPending,
  isFulfilled,
  isRejected,
} from "@reduxjs/toolkit";
import { DELETE, GET, POST, PUT } from "../../utils/api";
import { API } from "../../endpoints/common_endpoints";

/* ------------------------------------------------------------------ */
/* Addresses                                                           */
/* ------------------------------------------------------------------ */

export const createOrderAddress = createAsyncThunk(
  "create-order-addrress",
  async ({ data }, { rejectWithValue }) => {
    try {
      return await POST(API.ORDER_ADDRESS, data);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

// FIX: unique action type (was a duplicate of createOrderAddress)
export const EditOrderAddress = createAsyncThunk(
  "edit-order-address",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      return await PUT(`${API.ORDER_ADDRESS}/${id}`, data);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const getAllOrderAddress = createAsyncThunk(
  "get-order-addrress",
  async (data, { rejectWithValue }) => {
    try {
      return await GET(API.ORDER_ADDRESS);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const getAllSippingOrderAddress = createAsyncThunk(
  "get-sipping-order-addrress",
  async (data, { rejectWithValue }) => {
    try {
      return await GET(API.ORDER_ADDRESS + "/shipping");
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const getSingleOrderAddress = createAsyncThunk(
  "get-order-address-single",
  async ({ id }, { rejectWithValue }) => {
    try {
      return await GET(API.ORDER_ADDRESS + `/${id}`);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const deleteAddress = createAsyncThunk(
  "delete-address",
  async ({ id }, { rejectWithValue }) => {
    try {
      return await DELETE(API.ORDER_ADDRESS + `/${id}`);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

/* ------------------------------------------------------------------ */
/* Orders                                                              */
/* ------------------------------------------------------------------ */

export const createOrder = createAsyncThunk(
  "create-order",
  async (data, { rejectWithValue }) => {
    try {
      const response = await POST(API.ORDER, data);
      if (response?.success) {
        return response;
      }
      return rejectWithValue(response);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

// Public Razorpay key id served by the backend (GET /orders/razorpay-key)
export const getRazorpayKey = createAsyncThunk(
  "order/getRazorpayKey",
  async (_, { rejectWithValue }) => {
    try {
      return await GET(API.ORDER + "/razorpay-key");
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const getAllOrder = createAsyncThunk(
  "get-all-orders",
  async ({ order_status_id = "" } = {}, { rejectWithValue }) => {
    try {
      return await GET(API.ORDER + `?order_status_id=${order_status_id || ""}`);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const getAllDeliverOrder = createAsyncThunk(
  "get-all-orders-deliver",
  async ({ order_status_id = "" } = {}, { rejectWithValue }) => {
    try {
      return await GET(API.ORDER + `?order_status_id=${order_status_id || ""}`);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const getAllReturnOrder = createAsyncThunk(
  "get-all-return-orders",
  async ({ order_status_id = "" } = {}, { rejectWithValue }) => {
    try {
      return await GET(
        API.ORDER + `/return/order?order_status_id=${order_status_id || ""}`,
      );
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const getAllCancelOrder = createAsyncThunk(
  "get-all-cancel-orders",
  async ({ order_status_id = "" } = {}, { rejectWithValue }) => {
    try {
      return await GET(
        API.ORDER + `/cancel/order?order_status_id=${order_status_id || ""}`,
      );
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const getOrder = createAsyncThunk(
  "get-order",
  async ({ id, order_status_id }, { rejectWithValue }) => {
    try {
      return await GET(API.ORDER + `/${id}?order_status_id=${order_status_id}`);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const getSingleReturnOrder = createAsyncThunk(
  "get-retrun-order",
  async ({ id, order_status_id }, { rejectWithValue }) => {
    try {
      return await GET(
        API.ORDER + `/return/${id}?order_status_id=${order_status_id}`,
      );
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const getSingleCancelOrder = createAsyncThunk(
  "get-cancel-order",
  async ({ id, order_status_id }, { rejectWithValue }) => {
    try {
      return await GET(
        API.ORDER + `/cancel/${id}?order_status_id=${order_status_id}`,
      );
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const cancelOrder = createAsyncThunk(
  "cancel-order",
  async ({ order_id, data }, { rejectWithValue }) => {
    try {
      return await POST(API.ORDER_CANCEL + `/${order_id}`, data);
    } catch (error) {
      console.error("Cancel order failed:", error);
      return rejectWithValue(
        error.response?.data || error.message || "Something went wrong",
      );
    }
  },
);

// Legacy "pay after order" flow. The current checkout does not use it.
export const PaymentOnline = createAsyncThunk(
  "payment-online",
  async ({ id, payment_id }, { rejectWithValue }) => {
    try {
      return await POST(API.PaymentOnline + `/${id}`, { payment_id });
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const returnOrder = createAsyncThunk(
  "return-order",
  async ({ order_id, data }, { rejectWithValue }) => {
    try {
      return await POST(API.ORDER_RETURN + `/${order_id}`, data);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const createOrderMessage = createAsyncThunk(
  "create-order-message",
  async (data, { rejectWithValue }) => {
    try {
      return await POST(API.ORDER + "/message", data);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const getOrderMessage = createAsyncThunk(
  "get-order-message",
  async (id, { rejectWithValue }) => {
    try {
      return await GET(API.ORDER + "/message/data");
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const CouponApplyed = createAsyncThunk(
  "coupon-applyed",
  async (data, { rejectWithValue }) => {
    try {
      const response = await POST(API.COUPON_APPLYED, data);
      if (response?.success) {
        return response;
      }
      return rejectWithValue(response);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const OrderCalculation = createAsyncThunk(
  "order-calculation",
  async (data, { rejectWithValue }) => {
    try {
      const response = await POST(API.ORDER_CALCULATION, data);
      if (response?.success) {
        return response;
      }
      return rejectWithValue(response);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

/* ------------------------------------------------------------------ */
/* Slice                                                               */
/* ------------------------------------------------------------------ */

// Every thunk here drives the shared `status` / `error` fields.
const allThunks = [
  createOrderAddress,
  EditOrderAddress,
  getAllOrderAddress,
  getAllSippingOrderAddress,
  getSingleOrderAddress,
  deleteAddress,
  createOrder,
  getRazorpayKey,
  getAllOrder,
  getAllDeliverOrder,
  getAllReturnOrder,
  getAllCancelOrder,
  getOrder,
  getSingleReturnOrder,
  getSingleCancelOrder,
  cancelOrder,
  PaymentOnline,
  returnOrder,
  createOrderMessage,
  getOrderMessage,
  CouponApplyed,
  OrderCalculation,
];

const orderSlice = createSlice({
  name: "order",
  initialState: {
    allOrders: [],
    allDeliverOrders: [],
    allReturnOrders: [],
    allCancelOrders: [],
    allOrdersAddress: [],
    allSppingOrdersAddress: [],
    orderAddress: {},
    order: {},
    returnOrder: {},
    cancelOrder: {},
    orderMessage: [],
    orderCalculation: {},
    all_coupons: [],
    razorpayKey: null,
    error: null,
    status: "idle",
  },
  reducers: {},
  extraReducers: (builder) => {
    // Cases that store data. Cases must be added before any matcher.
    builder
      .addCase(getRazorpayKey.fulfilled, (state, action) => {
        // adjust the path if your GET helper returns a different shape
        state.razorpayKey = action.payload?.data?.key_id || null;
      })
      .addCase(getAllOrder.fulfilled, (state, action) => {
        state.allOrders = action.payload?.data || [];
      })
      .addCase(getAllDeliverOrder.fulfilled, (state, action) => {
        state.allDeliverOrders = action.payload?.data || [];
      })
      .addCase(getAllReturnOrder.fulfilled, (state, action) => {
        state.allReturnOrders = action.payload?.data || [];
      })
      .addCase(getAllCancelOrder.fulfilled, (state, action) => {
        state.allCancelOrders = action.payload?.data || [];
      })
      .addCase(getOrder.fulfilled, (state, action) => {
        state.order = action.payload?.data || {};
      })
      .addCase(getSingleReturnOrder.fulfilled, (state, action) => {
        state.returnOrder = action.payload?.data || {};
      })
      .addCase(getSingleCancelOrder.fulfilled, (state, action) => {
        state.cancelOrder = action.payload?.data || {};
      })
      .addCase(getAllOrderAddress.fulfilled, (state, action) => {
        state.allOrdersAddress = action.payload || [];
      })
      .addCase(getAllSippingOrderAddress.fulfilled, (state, action) => {
        state.allSppingOrdersAddress = action.payload || [];
      })
      .addCase(getSingleOrderAddress.fulfilled, (state, action) => {
        state.orderAddress = action.payload?.data || {};
      })
      .addCase(getOrderMessage.fulfilled, (state, action) => {
        state.orderMessage = action.payload?.data || [];
      })
      .addCase(OrderCalculation.fulfilled, (state, action) => {
        state.orderCalculation = action.payload?.data || {};
      });

    // Shared loading / success / error handling for every thunk.
    builder
      .addMatcher(isPending(...allThunks), (state) => {
        state.status = "loading";
      })
      .addMatcher(isFulfilled(...allThunks), (state) => {
        state.status = "succeeded";
      })
      .addMatcher(isRejected(...allThunks), (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      });
  },
});

export default orderSlice.reducer;